import React, { useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import AddModal from './AddModal/AddModal';
import AssignRoleModal from './AssignRoleModal';
import { laptopAPI, inventoryAPI } from '../services/api';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import BeautifiedErrorAlert from './ui/BeautifiedErrorAlert';
import AdminProfitDashboard from './analytics/AdminProfitDashboard';
import ActuatorDashboard from './analytics/ActuatorDashboard';
import SwaggerDashboard from './analytics/SwaggerDashboard';
import AdminVideoDashboard from './analytics/AdminVideoDashboard';
import AdminFeedbackDashboard from './analytics/AdminFeedbackDashboard';
import { Dialog, DialogContent } from './ui/dialog';
import SuccessScreen from './ui/SuccessScreen';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { Package, Users, ShoppingCart, TrendingUp, AlertTriangle, FileText, Activity, Server, FileJson, Youtube, UserCog, MessageSquare, Plus, LayoutDashboard, PackagePlus, FolderPlus, Layers, Box, DollarSign, Loader2, Download, FileSpreadsheet, AlertCircle, Edit, Trash2, Check, X, Minus } from 'lucide-react';

const AdminDashboard = ({ pendingCount = 0, onShowPendingModal }) => {
  // Stats state
  const [stats, setStats] = useState({
    totalLaptops: 0,
    totalStock: 0,
    totalValue: 0,
    totalBrands: 0,
    lowStockItems: 0,
    brands: []
  });

  // Category & Product state
  const [categories, setCategories] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loadingCategories, setLoadingCategories] = useState({});

  // Pagination State for Categories
  const [categoryPage, setCategoryPage] = useState(0);
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const CATEGORY_PAGE_SIZE = 10;

  // Modal / Misc state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [showProfitDashboard, setShowProfitDashboard] = useState(false);
  const [showActuatorDashboard, setShowActuatorDashboard] = useState(false);
  const [showSwaggerDashboard, setShowSwaggerDashboard] = useState(false);
  const [showVideoDashboard, setShowVideoDashboard] = useState(false);
  const [showFeedbackDashboard, setShowFeedbackDashboard] = useState(false);
  const [modalType, setModalType] = useState('product'); // 'product' or 'category'
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [canAssignRoles, setCanAssignRoles] = useState(false);
  
  // Unified Message State
  const [activeMessage, setActiveMessage] = useState(null); // { type: 'success' | 'error', title: '', body: '' }
  const [countdown, setCountdown] = useState(3);

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR'
  });
  // Pastel palette for category cards
  const cardPastel = [
    {
      bg: 'bg-rose-100 dark:bg-rose-900/20',
      border: 'border-rose-400 dark:border-rose-600',
      text: 'text-neutral-900 dark:text-neutral-100',
    },
    {
      bg: 'bg-sky-100 dark:bg-sky-900/20',
      border: 'border-sky-400 dark:border-sky-600',
      text: 'text-neutral-900 dark:text-neutral-100',
    },
    {
      bg: 'bg-green-100 dark:bg-green-900/20',
      border: 'border-green-400 dark:border-green-600',
      text: 'text-neutral-900 dark:text-neutral-100',
    },
    {
      bg: 'bg-violet-100 dark:bg-violet-900/20',
      border: 'border-violet-400 dark:border-violet-600',
      text: 'text-neutral-900 dark:text-neutral-100',
    },
    {
      bg: 'bg-yellow-100 dark:bg-yellow-900/20',
      border: 'border-yellow-400 dark:border-yellow-600',
      text: 'text-neutral-900 dark:text-neutral-100',
    }
  ];

  const searchRef = React.useRef(null);

  // Initial load
  useEffect(() => {
    loadStats();
    loadCategories(0, true);

    // Check user roles for Assign Role capability
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.roles && Array.isArray(user.roles)) {
          const hasAccess = user.roles.some(role =>
            role === 'ADMIN' || role === 'IT' ||
            role === 'ROLE_ADMIN' || role === 'ROLE_IT'
          );
          setCanAssignRoles(hasAccess);
        }
      } catch (e) {
        console.error("Error parsing user from local storage", e);
      }
    }
  }, []);

  useEffect(() => {
    if (expandedCategories.includes('Laptops') && !productsByCategory['Laptops']) {
      fetchProductsForCategory('Laptops');
    }
  }, [expandedCategories, productsByCategory]);

  useEffect(() => {
    let timer;
    if (activeMessage?.type === 'success' && countdown > 0) {
      timer = setInterval(() => setCountdown(c => c - 1), 1000);
    } else if (activeMessage?.type === 'success' && countdown === 0) {
      setActiveMessage(null);
      setCountdown(3);
    }
    return () => clearInterval(timer);
  }, [activeMessage, countdown]);

  const showSuccess = (msg, subMsg = "Database synchronized...") => {
    setActiveMessage({ type: 'success', title: msg, body: subMsg });
    setCountdown(3);
  };

  const showError = (msg) => {
    setActiveMessage({ type: 'error', title: "Action Required", body: msg });
  };

  // Real-time Dashboard Sync via WebSockets
  useEffect(() => {
    // Determine the base URL for WebSockets.
    // React's proxy in package.json handles /ws during development.
    const socketUrl = '/ws';

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      debug: (str) => {
        // console.log('STOMP Debug: ' + str);
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    stompClient.onConnect = (frame) => {
      console.log('Connected to Dashboard Sync Broker: ' + frame);
      stompClient.subscribe('/topic/dashboard', (message) => {
        console.log('Received real-time SYNC broadcast: ' + message.body);
        if (message.body === 'SYNC') {
          // Trigger silent backgrounds refresh
          loadStats();
          loadCategories(0, true);
          // If a category is expanded, refresh its products too
          if (expandedCategories.length > 0) {
            expandedCategories.forEach(catName => {
               fetchProductsForCategory(catName);
            });
          }
        }
      });
    };

    stompClient.onStompError = (frame) => {
      console.error('Broker reported error: ' + frame.headers['message']);
      console.error('Additional details: ' + frame.body);
    };

    stompClient.activate();

    return () => {
      if (stompClient) stompClient.deactivate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCategories]);

  const loadCategories = async (page = 0, reset = false) => {
    try {
      setIsCategoryLoading(true);
      const params = {
        pageNumber: page,
        pageSize: CATEGORY_PAGE_SIZE,
        sortBy: 'categoryName',
        sortOrder: 'ASC'
      };

      const response = await inventoryAPI.getCategories(params);

      let newCategories = [];
      let isLast = true;

      const responseData = response.data;

      if (responseData.content && Array.isArray(responseData.content)) {
        newCategories = responseData.content;
        if (responseData.lastPage !== undefined) {
          isLast = responseData.lastPage;
        } else if (responseData.last !== undefined) {
          isLast = responseData.last;
        } else {
          isLast = newCategories.length < CATEGORY_PAGE_SIZE;
        }
      } else if (Array.isArray(responseData)) {
        newCategories = responseData;
        isLast = newCategories.length < CATEGORY_PAGE_SIZE;
      }

      // Process categories to ensure they are objects { categoryId, categoryName }
      const processedCategories = newCategories.map(c => {
        if (typeof c === 'string') {
          // Should not happen with new backend, but fallback
          return { categoryId: Math.random(), categoryName: c };
        }
        return c;
      });

      setCategories(prev => reset ? processedCategories : [...prev, ...processedCategories]);
      setHasMoreCategories(!isLast);
      setCategoryPage(page);

    } catch (err) {
      console.error('Error loading categories:', err);
      const errorMessage = err.response?.data?.message || err.message;
      if (errorMessage === 'CATEGORIES HAS NOT BEEN CREATED TILL NOW!!!') {
        // Treat as empty state, not an error
        setCategories([]);
        setHasMoreCategories(false);
      } else {
        showError(errorMessage || 'Failed to load categories. Please try refreshing.');
      }
    } finally {
      setIsCategoryLoading(false);
    }
  };

  const handleLoadMoreCategories = () => {
    loadCategories(categoryPage + 1, false);
  };

  const loadStats = async () => {
    try {
      const response = await laptopAPI.getTotalProfit();
      const responseForStockSummary = await inventoryAPI.getStockSummary();
      setStats({
        totalLaptops: 0,
        totalStock: responseForStockSummary.data.totalQuantity,
        totalValue: responseForStockSummary.data.totalValue,
        totalBrands: 0,
        lowStockItems: 0,
        brands: [],
        totalNetProfit: response.data.totalNetProfit
      });
    } catch (err) {
      console.error('Error loading stats:', err);
      // We don't necessarily want to block the whole UI if just stats fail, 
      // but let's at least show a message.
      showError('Failed to load inventory statistics.');
    }
  };

  const fetchProductsForCategory = async (category) => {
    if (!category) return;
    setLoadingCategories(prev => ({ ...prev, [category]: true }));
    try {
      const response = await inventoryAPI.getProducts({ category, limit: 100 });

      let products = [];
      if (response.data && response.data.content) {
        products = response.data.content;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      } else if (response.data && response.data.data) {
        products = response.data.data;
      }

      setProductsByCategory(prev => ({
        ...prev,
        [category]: products
      }));
    } catch (err) {
      console.error(`Error loading products for ${category}:`, err);
      showError(`Failed to load products for ${category}.`);
      setProductsByCategory(prev => ({ ...prev, [category]: [] }));
    } finally {
      setLoadingCategories(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleExpandCategory = (categoryName) => {
    const wasExpanded = expandedCategories.includes(categoryName);
    const newExpanded = expandedCategories[0] === categoryName ? [] : [categoryName];
    setExpandedCategories(newExpanded);
    if (!wasExpanded && newExpanded.includes(categoryName)) {
      fetchProductsForCategory(categoryName);
    }
    if (newExpanded.length > 0) {
      searchRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const filteredCategories = categories
    .filter(cat => (cat.categoryName || '').toLowerCase().includes(categorySearchTerm.toLowerCase()))
    .sort((a, b) => (a.inStockCount || 0) - (b.inStockCount || 0));

  const handleUpdateStock = async (id, newStock, oldStock, skuProductCodes = null) => {
    try {
      await laptopAPI.updateLaptopStock(id, newStock, skuProductCodes);
      const message = newStock > oldStock ? "Stock Incremented Successfully!" :
                      newStock < oldStock ? "Stock Decremented Successfully!" :
                      "Stock Adjusted Successfully!";
      showSuccess(message);
      fetchProductsForCategory(expandedCategories[0]);
      
      // Update local category state dynamically to prevent wiping pagination
      setCategories(prev => prev.map(cat => 
        cat.categoryName === expandedCategories[0] 
          ? { ...cat, inStockCount: Math.max(0, (cat.inStockCount || 0) + (newStock - oldStock)) } 
          : cat
      ));
      
      loadStats();
    } catch (err) {
      console.error('Failed to update stock:', err);
      showError(err.response?.data?.message || err.message || 'Failed to update stock');
    }
  };

  const handleDelete = async (id) => {
    try {
      setActiveMessage(null);
      await laptopAPI.deleteLaptop(id);
      showSuccess("Product deleted successfully!");
      
      // We don't have the explicit stock amount easily here to deduct from inStockCount, 
      // but since we usually only delete when fixing mistakes, a background sync from WebSockets will fix it eventually.
      // Instead of resetting the pagination with loadCategories(0, true), we'll let it be.
      loadStats();
      
      if (expandedCategories.length > 0) {
        fetchProductsForCategory(expandedCategories[0]);
      }

      setProductsByCategory(prev => {
        const newState = { ...prev };
        const currentCategory = expandedCategories[0];

        if (currentCategory && newState[currentCategory]) {
          newState[currentCategory] = newState[currentCategory].filter(p => (p._id || p.productId) !== id);
        } else {
          // Fallback if for some reason we are not in a category view or multiple exist
          Object.keys(newState).forEach(key => {
            if (Array.isArray(newState[key])) {
              newState[key] = newState[key].filter(p => (p._id || p.productId) !== id);
            }
          });
        }
        return newState;
      });
    } catch (err) {
      console.error("Failed to delete", err);
      showError(err.response?.data?.message || err.message || "Failed to delete item. Please try again.");
    }
  };

  const handleAddProduct = async (newProduct) => {
    if (!newProduct) {
      // Refresh case (e.g., after bulk upload)
      loadStats();
      loadCategories(0, true);
      if (expandedCategories.length > 0) {
        fetchProductsForCategory(expandedCategories[0]);
      }
      return;
    }
    try {
      await laptopAPI.createProduct(newProduct);
      
      showSuccess("Product synchronized with system registry.");
      loadStats();
      
      // Update local state dynamically to handle "No Stock" -> "In Stock" transition
      setCategories(prev => prev.map(cat => 
        cat.categoryId === newProduct.categoryId 
          ? { ...cat, inStockCount: (cat.inStockCount || 0) + (newProduct.stockQty || 0) } 
          : cat
      ));

      // Auto-expand the category and fetch products so it appears instantly
      if (expandedCategories[0] !== newProduct.categoryName) {
         setExpandedCategories([newProduct.categoryName]);
      }
      fetchProductsForCategory(newProduct.categoryName);

    } catch (err) {
      console.error('Failed to add product:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add product. Please try again.';
      showError(errorMessage);
      throw err; // Re-throw so AddModal can handle it
    }
  };

  const handleAddCategory = async (categoryData) => {
    try {
      setActiveMessage(null);
      if (categoryToEdit) {
        await inventoryAPI.updateCategory(categoryToEdit.categoryId, categoryData);
        // Handled by AddModal
      } else {
        await inventoryAPI.createCategory(categoryData);
        // Handled by AddModal
      }
      loadCategories(0, true);
      // setShowAddModal(false);
      setCategoryToEdit(null);
    } catch (err) {
      console.error('Failed to save category:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save category. Please try again.';
      showError(errorMessage);
      throw err;
    }
  };

  const handleEditCategory = (e, category) => {
    e.stopPropagation();
    setCategoryToEdit(category);
    setModalType('category');
    setShowAddModal(true);
  };

  const handleDeleteCategory = async (e, category) => {
    e.stopPropagation();
    
    if ((category.inStockCount || 0) > 0) {
      showError(`Cannot delete "${category.categoryName}". Registry indicates ${category.inStockCount} active products are still associated with this catalog.`);
      return;
    }

    if (window.confirm(`Are you sure you want to permanently decommission the "${category.categoryName}" category? This action is irreversible.`)) {
      try {
        showSuccess("Category successfully decommissioned from system registry.");
        loadCategories(0, true);
        loadStats();
      } catch (err) {
        console.error("Failed to delete category", err);
        const errorMessage = err.response?.data?.message || err.message || "Protocol failure during category decommission.";
        showError(errorMessage);
      }
    }
  };

  const handleEdit = async (id, updatedProduct) => {
    try {
      await inventoryAPI.updateProduct(id, updatedProduct);

      setProductsByCategory(prev => {
        const newState = { ...prev };

        for (const category in newState) {
          const productIndex = newState[category].findIndex(
            p => (p.productId || p._id) === id
          );

          if (productIndex !== -1) {
            newState[category] = [
              ...newState[category].slice(0, productIndex),
              { ...newState[category][productIndex], ...updatedProduct },
              ...newState[category].slice(productIndex + 1)
            ];
            break;
          }
        }

        return newState;
      });

      loadStats();
      // Handled by EditProductModal
    } catch (err) {
      console.error('Failed to update product:', err);
      showError(err.response?.data?.message || err.message || 'Failed to update product');
    }
  };

  const handleDownloadReport = async () => {
    try {
      setActiveMessage(null);
      const response = await laptopAPI.getPendingOrders();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      showError('Failed to download report. Please ensure the backend is running.');
    }
  };

  return (
    <div className="space-y-6">
      <Dialog open={!!activeMessage} onOpenChange={() => setActiveMessage(null)}>
        <DialogContent className="max-w-md p-0 rounded-[3rem] border-0 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            {activeMessage?.type === 'success' ? (
                <SuccessScreen 
                    message={activeMessage.title}
                    subMessage={activeMessage.body}
                    countdown={countdown}
                    onAction={() => setActiveMessage(null)}
                />
            ) : (
                <div className="p-0">
                    <BeautifiedErrorAlert 
                        error={activeMessage?.body}
                        onDismiss={() => setActiveMessage(null)}
                    />
                </div>
            )}
        </DialogContent>
      </Dialog>

      <div className="flex flex-col gap-6">

        {/* Priority Attention Card - Ultra Premium Design */}
        {pendingCount > 0 && (
          <div
            onClick={onShowPendingModal}
            className="group cursor-pointer relative overflow-hidden rounded-3xl transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl animate-in slide-in-from-top-4"
          >
            {/* Main Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF3D00] to-[#FF9100] shadow-lg"></div>

            {/* Glass Overlays for Depth */}
            <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]"></div>
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none mix-blend-overlay"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/5 rounded-full blur-2xl -ml-20 -mb-20 pointer-events-none"></div>

            {/* Pattern Overlay (Subtle Dots) */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>

            {/* Content Container */}
            <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">

              <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto">
                {/* 3D Icon Container */}
                <div className="relative h-14 w-14 sm:h-16 sm:w-16 flex-shrink-0 flex items-center justify-center bg-white rounded-2xl shadow-xl transform group-hover:rotate-6 transition-transform duration-500">
                  <div className="absolute inset-0 bg-white/50 rounded-2xl animate-ping opacity-20"></div>
                  <AlertTriangle className="h-7 w-7 sm:h-8 sm:w-8 text-[#FF3D00] drop-shadow-sm" />
                  {/* Notification Badge on Icon */}
                  <span className="absolute -top-2 -right-2 h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center bg-slate-900 text-white text-[10px] font-bold rounded-full border-2 border-[#FF9100] shadow-sm">
                    !
                  </span>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-black/20 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-widest rounded-full backdrop-blur-md border border-white/10 shadow-inner">
                      Urgent Action
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-white leading-none tracking-tight drop-shadow-sm">
                    {pendingCount} Pending Payment{pendingCount !== 1 ? 's' : ''}
                  </h3>
                  <p className="text-white/90 font-medium text-xs sm:text-sm flex items-center gap-2">
                    Requires immediate verification
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                  </p>
                </div>
              </div>

              {/* Action Button */}
              <div className="w-full sm:w-auto flex justify-stretch sm:justify-end mt-2 sm:mt-0">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-white text-[#FF3D00] hover:bg-slate-50 border-0 shadow-xl hover:shadow-2xl transition-all duration-300 font-bold px-8 rounded-xl group-hover:scale-105"
                >
                  Review Now
                  <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                </Button>
              </div>
            </div>

            {/* Shine Effect on Hover */}
            <div className="absolute inset-0 transform -translate-x-full group-hover:animate-shine pointer-events-none">
              <div className="absolute inset-0 transform -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent w-1/2 h-full"></div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-2">
          <div className="space-y-1">
            <h2 className="text-3xl font-black tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-3">
                <Package className="w-8 h-8 text-indigo-600" />
                Dynamic Inventory Control
            </h2>
            <div className="flex items-center gap-2 px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-700/50 rounded-full w-fit">
                <Plus className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                <p className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                  Central Asset Management Hub
                </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4">
          {canAssignRoles && (
            <button
              className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-sky-500/10 hover:border-sky-200 transition-all duration-300"
              onClick={() => setShowAssignRoleModal(true)}
            >
              <div className="p-3 rounded-2xl bg-sky-50 text-sky-600 group-hover:scale-110 transition-transform">
                <UserCog className="h-6 w-6" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Roles</span>
            </button>
          )}

          <button
            className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-emerald-500/10 hover:border-emerald-200 transition-all duration-300"
            onClick={() => setShowProfitDashboard(true)}
          >
            <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-600 group-hover:scale-110 transition-transform">
                <TrendingUp className="h-6 w-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Profits</span>
          </button>

          {canAssignRoles && (
            <>
              <button
                className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-amber-500/10 hover:border-amber-200 transition-all duration-300"
                onClick={() => setShowActuatorDashboard(true)}
              >
                <div className="p-3 rounded-2xl bg-amber-50 text-amber-600 group-hover:scale-110 transition-transform">
                    <Activity className="h-6 w-6" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Health</span>
              </button>
              <button
                className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-purple-500/10 hover:border-purple-200 transition-all duration-300"
                onClick={() => setShowSwaggerDashboard(true)}
              >
                <div className="p-3 rounded-2xl bg-purple-50 text-purple-600 group-hover:scale-110 transition-transform">
                    <Server className="h-6 w-6" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">API</span>
              </button>
            </>
          )}

          <button
            className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-red-500/10 hover:border-red-200 transition-all duration-300"
            onClick={() => setShowVideoDashboard(true)}
          >
            <div className="p-3 rounded-2xl bg-red-50 text-red-600 group-hover:scale-110 transition-transform">
                <Youtube className="h-6 w-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Videos</span>
          </button>

          <button
            className="group flex flex-col items-center justify-center gap-3 p-4 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-black/5 hover:shadow-indigo-500/10 hover:border-indigo-200 transition-all duration-300"
            onClick={() => setShowFeedbackDashboard(true)}
          >
            <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600 group-hover:scale-110 transition-transform">
                <MessageSquare className="h-6 w-6" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Feedback</span>
          </button>

          <button
            className="group flex flex-col items-center justify-center gap-3 p-4 bg-black rounded-3xl shadow-2xl shadow-black/20 hover:bg-slate-800 active:scale-95 transition-all duration-300 col-span-1"
            onClick={() => { setShowAddModal(true); setModalType('product'); }}
          >
            <div className="p-3 rounded-2xl bg-white/20 text-white group-hover:rotate-12 transition-transform">
                <PackagePlus className="h-7 w-7" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.1em] text-white/90">Add SKU</span>
          </button>

          <button
            className="group flex flex-col items-center justify-center gap-3 p-4 bg-[#FF7D00] rounded-3xl shadow-2xl shadow-orange-500/20 hover:bg-[#E66000] active:scale-95 transition-all duration-300 col-span-1"
            onClick={() => { setShowAddModal(true); setModalType('category'); setCategoryToEdit(null); }}
          >
            <div className="p-3 rounded-2xl bg-white/20 text-white group-hover:rotate-12 transition-transform">
                <Layers className="h-7 w-7" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.1em] text-white/90">Category</span>
          </button>
        </div>
      </div>

      {/* Global Stats Row - Ultimate Edition */}
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="relative overflow-hidden group border-0 shadow-2xl transition-all duration-500 hover:-translate-y-2">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400 opacity-95 group-hover:scale-110 transition-transform duration-700"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-white/20 transition-all"></div>
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-black uppercase tracking-widest text-blue-50">Inventory Volume</CardTitle>
            <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/20">
              <Box className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-4">
            <div className="text-4xl font-black text-white tracking-tighter mb-1 select-none">{stats.totalStock}</div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-200 animate-pulse"></span>
              <p className="text-[10px] font-bold text-blue-100 uppercase tracking-wider">Live SKU Count</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group border-0 shadow-2xl transition-all duration-500 hover:-translate-y-2">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-400 opacity-95 group-hover:scale-110 transition-transform duration-700"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-white/20 transition-all"></div>
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2 text-white">
            <CardTitle className="text-sm font-black uppercase tracking-widest text-emerald-50">Asset Valuation</CardTitle>
            <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/20">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-4 text-white">
            <div className="text-4xl font-black tracking-tighter mb-1 select-none">{formatter.format(stats.totalValue)}</div>
            <div className="flex items-center gap-2 text-emerald-100">
               <TrendingUp className="h-3 w-3" />
               <p className="text-[10px] font-bold uppercase tracking-wider">Estimated Market Value</p>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden group border-0 shadow-2xl transition-all duration-500 hover:-translate-y-2 lg:col-span-1 md:col-span-2">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-700 to-indigo-500 opacity-95 group-hover:scale-110 transition-transform duration-700"></div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-white/20 transition-all"></div>
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2 text-white">
            <CardTitle className="text-sm font-black uppercase tracking-widest text-indigo-50">Success Metrics</CardTitle>
            <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl shadow-inner border border-white/20">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
          </CardHeader>
          <CardContent className="relative pt-4 text-white">
            <div className="text-4xl font-black tracking-tighter mb-1 select-none">{formatter.format(stats.totalNetProfit)}</div>
            <div className="flex items-center gap-2 text-indigo-100">
              <Users className="h-3 w-3" />
              <p className="text-[10px] font-bold uppercase tracking-wider">Per Sales Performance</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card ref={searchRef} id="insights" className="bg-muted/50 border-dashed">
        <CardContent className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <FileSpreadsheet className="h-6 w-6 text-green-700 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Inventory Report</h3>
              <p className="text-sm text-muted-foreground">Download detailed insights, pending orders, and stock status in Excel format.</p>
            </div>
          </div>
          <Button
            onClick={handleDownloadReport}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Excel Report
          </Button>
        </CardContent>
      </Card>


      {/* Global Category Search - Ultimate Style */}
      <div className="w-full relative group">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Package className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
        </div>
        <Input
          id="category-search"
          type="text"
          placeholder="Filter catalogs by name, tags, or brand identity..."
          className="h-16 pl-12 pr-4 rounded-2xl border-slate-200 bg-white shadow-xl shadow-black/5 focus:ring-4 focus:ring-indigo-500/10 font-bold text-lg transition-all"
          value={categorySearchTerm}
          onChange={(e) => setCategorySearchTerm(e.target.value)}
        />
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center gap-2">
            <span className="hidden sm:inline px-2 py-1 bg-slate-100 rounded-md text-[10px] font-black uppercase tracking-widest text-slate-400 selection:bg-indigo-500">Ctrl + K</span>
        </div>
      </div>


      {/* Expanded Category Section at Top (if any) */}
      {expandedCategories.length === 1 && (() => {
        const catIndex = filteredCategories.findIndex(cat => cat.categoryName === expandedCategories[0]);
        const pal = cardPastel[catIndex % cardPastel.length];
        return (
          <div className="w-full mb-8">
            <div
              className={`flex items-center justify-between ${pal.bg} shadow-md rounded-t-lg px-6 py-3 transition-colors cursor-pointer hover:bg-opacity-80 border-b ${pal.border} ${pal.text}`}
              onClick={() => setExpandedCategories([])}
              role="button"
              tabIndex={0}
              aria-label="Collapse category"
            >
              <span className="text-lg font-bold select-none">{expandedCategories[0]}</span>
              <span className="flex items-center ml-2">
                <svg xmlns="http://www.w3.org/2000/svg" className={`w-6 h-6 ${pal.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
            <Card className={`shadow-lg w-full rounded-t-none border-t-0 ${pal.border}`}>
              <CardContent>
                {loadingCategories[expandedCategories[0]] ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">Loading products...</span>
                  </div>
                ) : (
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 py-4">
                    {(productsByCategory[expandedCategories[0]] && productsByCategory[expandedCategories[0]].length > 0) ? (
                      productsByCategory[expandedCategories[0]].map(product => (
                        <ProductCard
                          key={product._id || product.productId}
                          product={product}
                          onUpdateStock={handleUpdateStock}
                          onDelete={handleDelete}
                          onEdit={handleEdit}
                          onError={showError}
                        />
                      ))
                    ) : (
                      <div className="col-span-full text-center py-8 text-muted-foreground">
                        No items in this category yet.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Pastel, Playful Uniform Grid of Categories - Ultimate Upgrade */}
      {/* Sectional Split Layout: No Stock (Left) vs In Stock (Right) */}
      <div className="flex flex-col xl:flex-row gap-8 py-4">
        {/* Left Side: Replenishment Required (No Stock) */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2 px-1">
            <div className="h-2 w-2 rounded-full bg-rose-500 animate-pulse"></div>
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Replenishment Zone (No Stock)</h3>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
            {filteredCategories
              .filter(cat => (cat.inStockCount || 0) === 0 && expandedCategories[0] !== cat.categoryName)
              .map((category, idx) => {
                const pal = cardPastel[idx % cardPastel.length];
                return (
                  <div key={category.categoryName} className="relative group animate-in fade-in slide-in-from-left-4 duration-500">
                    <Card
                      className={cn(
                        "relative cursor-pointer transition-all duration-300 h-32 flex flex-col justify-between overflow-hidden",
                        pal.bg,
                        "border-0 shadow-sm hover:shadow-2xl hover:-translate-y-2 group"
                      )}
                      onClick={() => handleExpandCategory(category.categoryName)}
                    >
                      <div className={cn("absolute top-0 left-0 w-full h-1.5", pal.border.replace('border-', 'bg-'))}></div>
                      <CardHeader className="flex-1 p-5 pb-2">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                  <span className={cn("text-xs font-black uppercase tracking-widest opacity-40", pal.text)}>Catalog</span>
                                  <Badge className="bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200/50 hover:bg-rose-500/20 px-1.5 py-0 h-4 text-[9px] font-black uppercase tracking-tighter">No Stock</Badge>
                              </div>
                              <h4 className={cn("text-lg font-black tracking-tight", pal.text)}>{category.categoryName}</h4>
                          </div>
                          <div className={cn("p-2 rounded-xl backdrop-blur-md bg-white/30 border border-white/20 shadow-sm group-hover:rotate-12 transition-transform", pal.text)}>
                              <Layers className="w-5 h-5" />
                          </div>
                        </div>
                      </CardHeader>
                      <div className="p-5 pt-0 flex justify-end gap-2 relative z-10">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl bg-white/50 hover:bg-white text-slate-700 shadow-sm" onClick={(e) => handleEditCategory(e, category)}><Edit className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl bg-rose-500/10 hover:bg-rose-500 text-rose-600 hover:text-white shadow-sm" onClick={(e) => handleDeleteCategory(e, category)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </Card>
                  </div>
                );
              })}
            {filteredCategories.filter(cat => (cat.inStockCount || 0) === 0).length === 0 && (
              <div className="col-span-full border-2 border-dashed border-emerald-500/20 rounded-3xl p-8 flex flex-col items-center justify-center text-center">
                <div className="p-3 bg-emerald-500/10 rounded-full mb-3"><Package className="h-6 w-6 text-emerald-500" /></div>
                <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Inventory Perfect</p>
                <p className="text-[10px] text-slate-400 mt-1 uppercase">All categories have active stock</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Sales Hub (In Stock) */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2 px-1">
            <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
            <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Sales Hub (In Stock)</h3>
          </div>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
            {filteredCategories
              .filter(cat => (cat.inStockCount || 0) > 0 && expandedCategories[0] !== cat.categoryName)
              .sort((a,b) => (b.inStockCount || 0) - (a.inStockCount || 0)) // Higher stock first in this section
              .map((category, idx) => {
                const pal = cardPastel[(idx + 2) % cardPastel.length];
                return (
                  <div key={category.categoryName} className="relative group animate-in fade-in slide-in-from-right-4 duration-500">
                    <Card
                      className={cn(
                        "relative cursor-pointer transition-all duration-300 h-32 flex flex-col justify-between overflow-hidden",
                        pal.bg,
                        "border-0 shadow-sm hover:shadow-2xl hover:-translate-y-2 group"
                      )}
                      onClick={() => handleExpandCategory(category.categoryName)}
                    >
                      <div className={cn("absolute top-0 left-0 w-full h-1.5", pal.border.replace('border-', 'bg-'))}></div>
                      <CardHeader className="flex-1 p-5 pb-2">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                  <span className={cn("text-xs font-black uppercase tracking-widest opacity-40", pal.text)}>Catalog</span>
                                  <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 hover:bg-emerald-500/20 px-1.5 py-0 h-4 text-[9px] font-black uppercase tracking-tighter">Qty: {category.inStockCount}</Badge>
                              </div>
                              <h4 className={cn("text-lg font-black tracking-tight", pal.text)}>{category.categoryName}</h4>
                          </div>
                          <div className={cn("p-2 rounded-xl backdrop-blur-md bg-white/30 border border-white/20 shadow-sm group-hover:rotate-12 transition-transform", pal.text)}>
                              <Layers className="w-5 h-5" />
                          </div>
                        </div>
                      </CardHeader>
                      <div className="p-5 pt-0 flex justify-end gap-2 relative z-10">
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl bg-white/50 hover:bg-white text-slate-700 shadow-sm" onClick={(e) => handleEditCategory(e, category)}><Edit className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl bg-rose-500/10 hover:bg-rose-500 text-rose-600 hover:text-white shadow-sm" onClick={(e) => handleDeleteCategory(e, category)}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </Card>
                  </div>
                );
              })}
            {filteredCategories.filter(cat => (cat.inStockCount || 0) > 0).length === 0 && (
              <div className="col-span-full border-2 border-dashed border-rose-500/20 rounded-3xl p-8 flex flex-col items-center justify-center text-center opacity-50">
                <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black">No Active Inventory</p>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Pagination for Categories */}
      {!categorySearchTerm && hasMoreCategories && (
        <Button
          onClick={handleLoadMoreCategories}
          disabled={isCategoryLoading}
          variant="outline"
          className="w-full"
        >
          {isCategoryLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            'Load More Categories'
          )}
        </Button>
      )}

      {filteredCategories.length === 0 && !isCategoryLoading && (
        <div className="text-center py-8 text-muted-foreground">
          No categories found matching "{categorySearchTerm}"
        </div>
      )}

      {showVideoDashboard && (
        <AdminVideoDashboard onClose={() => setShowVideoDashboard(false)} />
      )}

      {showAddModal && (
        <AddModal
          onClose={() => { setShowAddModal(false); setCategoryToEdit(null); }}
          onAdd={modalType === 'product' ? handleAddProduct : handleAddCategory}
          modalType={modalType}
          initialData={categoryToEdit}
        />
      )}

      {showAssignRoleModal && (
        <AssignRoleModal
          onClose={() => setShowAssignRoleModal(false)}
          onSuccess={() => {
            // Optional: Show success toast or refresh specific data if needed
            alert("Roles assigned successfully!");
          }}
        />
      )}

      {showProfitDashboard && (
        <AdminProfitDashboard onClose={() => setShowProfitDashboard(false)} />
      )}

      {showActuatorDashboard && (
        <ActuatorDashboard onClose={() => setShowActuatorDashboard(false)} />
      )}

      {showSwaggerDashboard && (
        <SwaggerDashboard onClose={() => setShowSwaggerDashboard(false)} />
      )}

      {showFeedbackDashboard && (
        <AdminFeedbackDashboard onClose={() => setShowFeedbackDashboard(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
